import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Phone,
  MapPin,
  Mail,
  Clock,
  Send,
  MessageSquare,
  CheckCircle,
  ChevronDown,
  User,
  HelpCircle,
  Car,
  Wrench,
  DollarSign,
  FileText,
  Sparkles,
  ArrowRight,
  Star,
  Shield,
  Zap,
  Heart,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import "./Contact.css";

// Scroll reveal hook
function useScrollReveal() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("revealed");
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" },
    );

    document.querySelectorAll(".scroll-reveal").forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);
}

function Contact() {
  useScrollReveal();

  const [formData, setFormData] = useState({
    name: "",
    inquiryType: "general",
    email: "",
    phone: "",
    preferredContact: "call",
    bestTime: "anytime",
    message: "",
    offers: false,
    terms: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSubmitted(true);
    }, 1500);
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        type === "checkbox" ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const inquiryOptions = [
    { value: "general", label: "General Inquiry", icon: HelpCircle },
    { value: "finance", label: "Financing", icon: DollarSign },
    { value: "parts", label: "Parts & Accessories", icon: Wrench },
    { value: "service", label: "Service & Maintenance", icon: Car },
  ];

  const contactMethods = [
    { value: "call", label: "Phone Call" },
    { value: "email", label: "Email" },
    { value: "text", label: "Text Message" },
  ];

  const bestTimes = [
    { value: "anytime", label: "Anytime" },
    { value: "morning", label: "Morning (9AM - 12PM)" },
    { value: "afternoon", label: "Afternoon (12PM - 5PM)" },
    { value: "evening", label: "Evening (5PM - 8PM)" },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <img
            src="/images/contact-hero-bg.jpg"
            alt="Customer service"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/95 via-gray-900/80 to-gray-900/60" />
        </div>

        {/* Animated Shapes */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 right-1/4 w-64 h-64 bg-red-500/10 rounded-full blur-3xl animate-pulse" />
          <div
            className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-yellow-500/10 rounded-full blur-3xl animate-pulse"
            style={{ animationDelay: "1s" }}
          />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-20 sm:pt-32 sm:pb-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="scroll-reveal">
              <Badge className="mb-6 bg-gradient-to-r from-red-600 to-red-500 text-white border-0 px-4 py-2">
                <MessageSquare className="w-4 h-4 mr-2" />
                Get In Touch
              </Badge>

              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6">
                WE'D LOVE TO <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-400 to-yellow-400">
                  HEAR FROM YOU
                </span>
              </h1>

              <p className="text-xl text-gray-300 mb-8 max-w-lg">
                Have questions about our vehicles, financing options, or
                services? Our dedicated team is here to help you every step of
                the way.
              </p>

              {/* Quick Contact Cards */}
              <div className="grid sm:grid-cols-2 gap-4">
                <a
                  href="tel:6043588081"
                  className="group bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/20 hover:bg-white/20 transition-all"
                >
                  <div className="w-12 h-12 bg-red-600 rounded-lg flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <p className="text-gray-400 text-sm">Call Us</p>
                  <p className="text-white font-semibold text-lg">
                    (604) 358-8081
                  </p>
                </a>

                <div className="group bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/20">
                  <div className="w-12 h-12 bg-yellow-600 rounded-lg flex items-center justify-center mb-3">
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                  <p className="text-gray-400 text-sm">Business Hours</p>
                  <p className="text-white font-semibold">Mon-Sat: 9AM-8PM</p>
                </div>
              </div>
            </div>

            {/* Right - Floating Info Card */}
            <div className="hidden lg:block scroll-reveal">
              <div className="bg-white/10 backdrop-blur-md rounded-3xl p-8 border border-white/20">
                <h3 className="text-2xl font-bold text-white mb-6">
                  Why Contact Us?
                </h3>
                <div className="space-y-4">
                  {[
                    { icon: Car, text: "Vehicle inquiries and test drives" },
                    { icon: DollarSign, text: "Financing and payment options" },
                    { icon: Wrench, text: "Service and maintenance questions" },
                    { icon: FileText, text: "Trade-in valuations" },
                  ].map((item, index) => (
                    <div key={index} className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-red-600/30 rounded-lg flex items-center justify-center">
                        <item.icon className="w-5 h-5 text-red-400" />
                      </div>
                      <span className="text-gray-300">{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="pt-28 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Left Column - Contact Info */}
            <div className="lg:col-span-2 scroll-reveal">
              <div className="sticky top-28">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  Contact <span className="text-red-600">Information</span>
                </h2>

                <p className="text-gray-600 mb-8 leading-relaxed">
                  We welcome your feedback and comments. Whether you have
                  questions about our inventory, need help with financing, or
                  want to schedule a service appointment, we're here to assist
                  you.
                </p>

                {/* Contact Cards */}
                <div className="space-y-4 mb-8">
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-lg transition-shadow group">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-red-600 transition-colors">
                        <Phone className="w-6 h-6 text-red-600 group-hover:text-white transition-colors" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">
                          Phone
                        </h4>
                        <a
                          href="tel:6043588081"
                          className="text-red-600 font-medium hover:underline"
                        >
                          (604) 358-8081
                        </a>
                        <p className="text-sm text-gray-500 mt-1">
                          Sales & Service
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-lg transition-shadow group">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-blue-600 transition-colors">
                        <Mail className="w-6 h-6 text-blue-600 group-hover:text-white transition-colors" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">
                          Email
                        </h4>
                        <a
                          href="mailto:info@bigboysmotorclub.com"
                          className="text-blue-600 font-medium hover:underline"
                        >
                          info@bigboysmotorclub.com
                        </a>
                        <p className="text-sm text-gray-500 mt-1">
                          General Inquiries
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-lg transition-shadow group">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-green-600 transition-colors">
                        <MapPin className="w-6 h-6 text-green-600 group-hover:text-white transition-colors" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">
                          Location
                        </h4>
                        <p className="text-gray-700">20308 Logan Avenue</p>
                        <p className="text-gray-700">Langley, BC V3A 4L7</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-lg transition-shadow group">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-yellow-600 transition-colors">
                        <Clock className="w-6 h-6 text-yellow-600 group-hover:text-white transition-colors" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">
                          Business Hours
                        </h4>
                        <div className="text-gray-700 space-y-1">
                          <p>Monday - Saturday: 9:00 AM - 8:00 PM</p>
                          <p>Sunday: 11:00 AM - 6:00 PM</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Trust Badges */}
                <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6">
                  <h4 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-yellow-400" />
                    Why Choose Us
                  </h4>
                  <div className="space-y-3">
                    {[
                      { icon: Star, text: "All credit situations welcome" },
                      {
                        icon: Shield,
                        text: "Bad credit, low credit, or no credit",
                      },
                      { icon: Zap, text: "New to Canada financing support" },
                      {
                        icon: Heart,
                        text: "Steady income? We may be able to help",
                      },
                    ].map((item, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-3 text-gray-300"
                      >
                        <item.icon className="w-4 h-4 text-red-400" />
                        <span className="text-sm">{item.text}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Contact Form */}
            <div className="lg:col-span-3 scroll-reveal">
              <div className="bg-white rounded-3xl shadow-xl border border-gray-200 overflow-hidden">
                {/* Form Header */}
                <div className="bg-gradient-to-r from-red-600 to-red-700 px-8 py-6">
                  <h3 className="text-2xl font-bold text-white flex items-center gap-3">
                    <Send className="w-6 h-6" />
                    Send Us a Message
                  </h3>
                  <p className="text-red-100 mt-2">
                    Fill out the form below and we'll get back to you within 24
                    hours.
                  </p>
                </div>

                {/* Form Content */}
                <div className="p-8">
                  {isSubmitted ? (
                    <div className="text-center py-12">
                      <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
                        <CheckCircle className="w-10 h-10 text-green-600" />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-3">
                        Message Sent!
                      </h3>
                      <p className="text-gray-600 mb-6">
                        Thank you for reaching out. Our team will contact you
                        within 24 hours.
                      </p>
                      <button
                        onClick={() => {
                          setIsSubmitted(false);
                          setFormData({
                            name: "",
                            inquiryType: "general",
                            email: "",
                            phone: "",
                            preferredContact: "call",
                            bestTime: "anytime",
                            message: "",
                            offers: false,
                            terms: false,
                          });
                        }}
                        className="inline-flex items-center gap-2 text-red-600 font-semibold hover:underline"
                      >
                        Send another message
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      {/* Name Field */}
                      <div className="form-group">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          First and Last Name{" "}
                          <span className="text-red-600">*</span>
                        </label>
                        <div className="relative">
                          <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                          <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            placeholder="John Smith"
                            className="w-full pl-12 pr-4 py-4 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:border-red-500 focus:ring-2 focus:ring-red-200 transition-all outline-none"
                          />
                        </div>
                      </div>

                      {/* Inquiry Type */}
                      <div className="form-group">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          What are you inquiring about?{" "}
                          <span className="text-red-600">*</span>
                        </label>
                        <div className="relative">
                          <select
                            name="inquiryType"
                            value={formData.inquiryType}
                            onChange={handleChange}
                            required
                            className="w-full pl-4 pr-12 py-4 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:border-red-500 focus:ring-2 focus:ring-red-200 transition-all outline-none appearance-none cursor-pointer"
                          >
                            {inquiryOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                        </div>
                      </div>

                      {/* Email & Phone Row */}
                      <div className="grid md:grid-cols-2 gap-6">
                        <div className="form-group">
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Email Address{" "}
                            <span className="text-red-600">*</span>
                          </label>
                          <div className="relative">
                            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                              type="email"
                              name="email"
                              value={formData.email}
                              onChange={handleChange}
                              required
                              placeholder="john@example.com"
                              className="w-full pl-12 pr-4 py-4 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:border-red-500 focus:ring-2 focus:ring-red-200 transition-all outline-none"
                            />
                          </div>
                        </div>

                        <div className="form-group">
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Phone Number
                          </label>
                          <div className="relative">
                            <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                              type="tel"
                              name="phone"
                              value={formData.phone}
                              onChange={handleChange}
                              placeholder="(604) 123-4567"
                              className="w-full pl-12 pr-4 py-4 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:border-red-500 focus:ring-2 focus:ring-red-200 transition-all outline-none"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Preferred Contact & Best Time Row */}
                      <div className="grid md:grid-cols-2 gap-6">
                        <div className="form-group">
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Preferred Contact Method
                          </label>
                          <div className="relative">
                            <select
                              name="preferredContact"
                              value={formData.preferredContact}
                              onChange={handleChange}
                              className="w-full pl-4 pr-12 py-4 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:border-red-500 focus:ring-2 focus:ring-red-200 transition-all outline-none appearance-none cursor-pointer"
                            >
                              {contactMethods.map((method) => (
                                <option key={method.value} value={method.value}>
                                  {method.label}
                                </option>
                              ))}
                            </select>
                            <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                          </div>
                        </div>

                        <div className="form-group">
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Best Time to Call?
                          </label>
                          <div className="relative">
                            <select
                              name="bestTime"
                              value={formData.bestTime}
                              onChange={handleChange}
                              className="w-full pl-4 pr-12 py-4 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:border-red-500 focus:ring-2 focus:ring-red-200 transition-all outline-none appearance-none cursor-pointer"
                            >
                              {bestTimes.map((time) => (
                                <option key={time.value} value={time.value}>
                                  {time.label}
                                </option>
                              ))}
                            </select>
                            <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                          </div>
                        </div>
                      </div>

                      {/* Message Field */}
                      <div className="form-group">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Dealer Message
                        </label>
                        <textarea
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          rows={4}
                          placeholder="Tell us how we can help you..."
                          className="w-full px-4 py-4 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:border-red-500 focus:ring-2 focus:ring-red-200 transition-all outline-none resize-none"
                        />
                      </div>

                      {/* Checkboxes */}
                      <div className="space-y-4">
                        <label className="flex items-start gap-3 cursor-pointer group">
                          <Checkbox
                            checked={formData.offers}
                            onCheckedChange={(checked) =>
                              setFormData((prev) => ({
                                ...prev,
                                offers: checked as boolean,
                              }))
                            }
                            className="mt-1"
                          />
                          <span className="text-sm text-gray-600 group-hover:text-gray-800 transition-colors">
                            Yes, send me exclusive offers and promotions from
                            Big Boys Motor Club and its partners. I can withdraw
                            my consent at any time.
                          </span>
                        </label>

                        <label className="flex items-start gap-3 cursor-pointer group">
                          <Checkbox
                            checked={formData.terms}
                            onCheckedChange={(checked) =>
                              setFormData((prev) => ({
                                ...prev,
                                terms: checked as boolean,
                              }))
                            }
                            className="mt-1"
                          />
                          <span className="text-sm text-gray-600 group-hover:text-gray-800 transition-colors">
                            By clicking "Send" I agree to receive communications
                            and share my personal information with Big Boys
                            Motor Club and its partners. I also agree to the{" "}
                            <Link
                              to="/terms-and-conditions"
                              className="text-red-600 hover:underline"
                            >
                              Terms and Conditions
                            </Link>{" "}
                            and the{" "}
                            <Link
                              to="/privacy-policy"
                              className="text-red-600 hover:underline"
                            >
                              Privacy Policy
                            </Link>
                            .
                          </span>
                        </label>
                      </div>

                      {/* Submit Button */}
                      <button
                        type="submit"
                        disabled={isSubmitting || !formData.terms}
                        className="w-full bg-gradient-to-r from-red-600 to-red-700 text-white font-bold py-4 px-8 rounded-xl hover:shadow-lg hover:shadow-red-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 group"
                      >
                        {isSubmitting ? (
                          <>
                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Send className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                            Send Message
                          </>
                        )}
                      </button>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Map Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="scroll-reveal bg-gray-900 rounded-3xl overflow-hidden">
          <div className="grid lg:grid-cols-3">
            <div className="p-8 lg:p-12">
              <h3 className="text-2xl font-bold text-white mb-4">
                Visit Our Showroom
              </h3>
              <p className="text-gray-400 mb-6">
                Come see our extensive inventory in person. Our friendly staff
                is ready to help you find your perfect vehicle.
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-3 text-gray-300">
                  <MapPin className="w-5 h-5 text-red-500" />
                  <span>20308 Logan Ave, Langley, BC</span>
                </div>
                <div className="flex items-center gap-3 text-gray-300">
                  <Phone className="w-5 h-5 text-red-500" />
                  <span>(604) 358-8081</span>
                </div>
              </div>
              <a
                href="https://maps.google.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 mt-6 text-red-400 font-semibold hover:text-red-300 transition-colors"
              >
                Get Directions
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
            <div className="lg:col-span-2 bg-gray-800 min-h-[300px] flex items-center justify-center">
              <div className="text-center">
                <MapPin className="w-16 h-16 text-red-500 mx-auto mb-4" />
                <p className="text-white font-semibold text-lg">
                  Big Boys Motor Club
                </p>
                <p className="text-gray-400">20308 Logan Ave, Langley, BC</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Contact;
